import { Platform } from 'react-native';
import { useEffect, useRef } from 'react';
import { calculateZoomValue } from '../helpers/zoom.helper';
import { IUserImageModel } from '../store/_models/userImage';
import { emit } from '../contexts/TransformEventBus';

const SENSITIVITY = 0.01;

type useWheelZoomProps = {
  isActive: boolean;
  userImage?: IUserImageModel;
};

export const useWheelZoom = ({ isActive = false, userImage }: useWheelZoomProps) => {
  // For wheel pinch logic:
  const pinchDeltaRef = useRef(0);
  const pinchActiveRef = useRef(false);
  const pinchBaseScaleRef = useRef(1);
  const pinchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (Platform.OS !== 'web') return;

    const handleWheel = (event: WheelEvent) => {
      // If ctrlKey is not pressed, no pinch-zoom
      if (!event.ctrlKey) return;

      if (!isActive) return;
      if (!userImage) return;

      // == Custom pinch logic starts here ==
      if (!pinchActiveRef.current) {
        pinchActiveRef.current = true;
        pinchDeltaRef.current = 0;
        pinchBaseScaleRef.current = userImage.scale;
      }

      pinchDeltaRef.current += event.deltaY;
      const factor = Math.exp(-pinchDeltaRef.current * SENSITIVITY);
      const rawScaleValue = pinchBaseScaleRef.current * factor;
      let newScaleValue = calculateZoomValue(rawScaleValue, userImage.minScale, userImage.maxScale);

      if (newScaleValue < userImage.minRelativeScale) newScaleValue = userImage.minRelativeScale;

      // userImage.setScale(newScaleValue);

      emit({
        type: 'temp-zoom',
        userImageId: userImage.id,
        payload: { absoluteScale: newScaleValue },
        source: 'gesture'
      });

      // Debounce finalize
      if (pinchTimeoutRef.current) {
        clearTimeout(pinchTimeoutRef.current);
      }
      pinchTimeoutRef.current = setTimeout(() => {
        pinchActiveRef.current = false;
        pinchDeltaRef.current = 0;

        // userImage.applyScale(userImage.scale);

        emit({
          type: 'apply-zoom',
          userImageId: userImage.id,
          payload: { absoluteScale: userImage.scale },
          source: 'gesture'
        });
      }, 100);
    };

    // Attach wheel listener (with passive:false so we can preventDefault)
    window.addEventListener('wheel', handleWheel, { passive: false });
    return () => {
      window.removeEventListener('wheel', handleWheel);
    };
  }, [isActive, userImage]);
};
